import { getStrings } from "@microsoft/shifts-experiences-shared";
import { ConstraintKey, constraintDisplayMap, constraintKeys } from "@microsoft/shifts-models-view";
import { SchedulingConstraintsPanelStore, schedulingConstraintsPanelActions } from "@microsoft/shifts-states-schedules";
import { DrawerProps } from "@microsoft/shifts-ui-core";
import { TeamStore } from "sh-team-store"; // Legacy import pattern for TeamStore

import { useCallbacks } from "../../callbacks";

/**
 * Interface for enabled scheduling constraint display data.
 */
export interface EnabledConstraintData {
    /**
     * The constraint key.
     */
    key: ConstraintKey;
    /**
     * The display label for the constraint.
     */
    label: string;
    /**
     * The value for the constraint.
     */
    value: number;
    /**
     * The unit for the constraint.
     */
    unit: "hours" | "days";
}

/**
 * Gets the drawer props for the scheduling rules panel.
 * @returns The drawer props for the scheduling rules panel.
 */
export const useDrawerProps = (): DrawerProps &
{
    /**
     * Handler for open state change.
     */
    onOpenChange: () => void;
} => {
    const store = SchedulingConstraintsPanelStore();

    return {
        open: store.isPanelOpen,
        onOpenChange: () => schedulingConstraintsPanelActions.closePanel(),
        position: "end" as const,
        size: "small" as const
    };
};

/**
 * Gets the scheduling rules data props for enabled constraints only.
 * @returns Array of enabled scheduling constraints with display data.
 */
export const useSchedulingRulesDataProps = (): EnabledConstraintData[] => {
    const teamStore = TeamStore();
    const team = teamStore.team;
    const constraints = team?.schedulingConstraints;

    if (!constraints) {
        return [];
    }

    const enabledConstraints: EnabledConstraintData[] = [];

    // Iterate through all constraint keys and add enabled ones
    constraintKeys.forEach((key: ConstraintKey) => {
        const constraint = constraints[key];
        if (constraint?.enabled && constraint.constraintValue != null) {
            const displayInfo = constraintDisplayMap[key];
            if (displayInfo) {
                enabledConstraints.push({
                    key,
                    label: getStrings("schedulingConstraints", displayInfo.labelKey),
                    value: constraint.constraintValue,
                    unit: displayInfo.unit
                });
            }
        }
    });

    return enabledConstraints;
};

/**
 * Gets the settings button props.
 * @returns The settings button props.
 */
export const useSettingsButtonProps = (): {
    /**
     * Handler for button click.
     */
    onClick: () => void;
} => {
    const callbacks = useCallbacks();

    return {
        onClick: callbacks.navigateToSettings
    };
};
